import React, {useEffect, useState} from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import axios from "axios";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {SERVICES} from "../../../common/constants/routes";
import {getUniqueKey} from "../../../utility/componentUtil";
import useWindowSize from "../../../utility/useWindowSize";
import CCOContainer from "../../fundamentals/CCOContainer";
import {
    addRequestParameters,
    mandatoryParametersPresent,
    PeriodIncreaseFormData,
    Props,
    renderFormErrorArea,
    renderFormHeadlineAndTitle,
    renderLoadingContainer,
    renderLobsterData,
    renderPicture,
    renderPrivacyInfo,
    renderSubmitButton,
    sendFormConfirmation,
    State
} from "../F006AmountIncreaseForm/amountIncreaseFormUtil";
import {useRouter} from "next/router";
import F006HiddenField from "../F006AmountIncreaseForm/F006HiddenField";
import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import {FORM_TYPES} from "tracking/events/forms";
import {useFormTracking} from "tracking/hooks/useFormTracking";


const renderPeriodText = (props: Props) => {
    return (
        <Grid
            data-cypress={"periodText"}
            size={{xs: 12, md: 12}}
            sx={{
                mb: {md: 0, xs: 0},
                pt: '0!important'
            }}>
            <Typography sx={{display: 'inline', lineHeight: '130%'}} variant={'h3'}>
                {props.sosAmountIncreaseForm.bankwireText}
            </Typography>
        </Grid>
    );
};

function F008PeriodIncreaseForm(props: Props) {
    const router = useRouter();
    const {t} = useTranslation();
    const {register, setValue, handleSubmit} = useForm<PeriodIncreaseFormData>(
        {mode: "onChange", shouldUnregister: true});

    const formId = props.id ?? null;
    const formName = (props.sosAmountIncreaseForm as any)?.subheading1 || null;
    const formType = FORM_TYPES.PERIOD_EXTENSION;
    const formTracking = useFormTracking({
                                             formId,
                                             formName,
                                             formType
                                         });
    const {lowerThanWindowLarge, lowerThanWindowSmall} = useWindowSize();

    const defaultState: Partial<State> = {
        lobsterDataLoaded: false,
        lobsterDataError: false
    };

    const [state, setState] = React.useState(defaultState);
    const [lobsterData, setLobsterData] = useState({} as any);
    const [focus, setFocus] = useState(false);
    const [clicked, setClicked] = useState(false);
    const [errorBox, setErrorBox] = useState(false);
    const [error, setError] = React.useState('');

    const errorMessage = props.donationContext.errorMessage || t("lobster_request.error_message");
    const errorElement = <Typography variant="h6" color="error">{errorMessage}</Typography>;

    function retrieveData(requestData: any, receivedData: boolean) {
        if (!receivedData && mandatoryParametersPresent(props, router.query)) {
            axios({
                      method: "post",
                      url: SERVICES.LOBSTER_REQUEST,
                      headers: {
                          'X-CSRF-Token': props.csrfToken
                      },
                      data: requestData
                  }).then(function (success) {
                const lobsterDataResponse = JSON.parse(success.data.responseBody);
                if (!(lobsterDataResponse && lobsterDataResponse.responseData &&
                      lobsterDataResponse.responseData[0])) {
                    setState({
                                 ...state,
                                 lobsterDataError: true
                             });
                    setError(errorMessage);
                    return;
                }
                setLobsterData(lobsterDataResponse);
                const firstLobsterDataObject = lobsterDataResponse.responseData[0];
                setState({
                             ...state,
                             lobsterDataLoaded: true
                         });
                setValue("CID", firstLobsterDataObject.CID);
                setValue("PSN", firstLobsterDataObject.PSN);
                setValue("CampaignID", router.query['CampaignID'] as string);
                setValue("formType", props.donationContext.actionParameters.formType);
                const uuid = getUniqueKey();
                setValue("donationUuid", uuid);
                setValue("isExtension", true);
            }).catch(function () {
                setState({
                             ...state,
                             lobsterDataError: true
                         });
                setError(errorMessage);
            });
        } else {
            setState({
                         ...state,
                         lobsterDataError: true
                     });
        }
    }

    useEffect(() => {
        let receivedData = false;
        const requestData: any = {
            csrfToken: props.csrfToken,
            id: props.id
        };
        addRequestParameters(props, requestData, router.query);
        retrieveData(requestData, receivedData);
        return () => {
            receivedData = true;
        };
        // eslint-disable-next-line
    }, []);

    const onInvalid = async () => {
        try {
            await formTracking.onSubmitError('frontend_validation_failed');
        } finally {
            formTracking.endInteraction();
        }
    };

    /**
     * Handle form submission based on selected payment method.
     * @param {any} formData - The form data to be submitted.
     * @return {void}
     */
    async function onSubmit(formData: PeriodIncreaseFormData) {
        try {
            await sendFormConfirmation(
                formData,
                setClicked,
                setErrorBox,
                'PeriodIncreaseForm',
                props.csrfToken,
                async () => {
                    await formTracking.onSubmitSuccess();
                },
                async (msg) => {
                    await formTracking.onSubmitError(msg ?? null);
                }
            );
        } finally {
            formTracking.endInteraction();
        }
    }

    return (
        <CCOComponentWrapper noTopMargin={true}>
            {!state.lobsterDataLoaded ?
                renderLoadingContainer(state, t) :
                <CCOContainer>
                    <form onSubmit={handleSubmit(onSubmit, onInvalid)}
                          onFocusCapture={() => formTracking.onFieldInteract()}
                          onClickCapture={() => formTracking.onFieldInteract()}>
                        <F006HiddenField register={register} name={"id"} value={props.id}/>
                        <F006HiddenField register={register} name={"csrfToken"}
                                         value={props.csrfToken}/>

                        <Grid container direction={"row"} spacing={5} sx={{
                            justifyContent: "left"
                        }}>
                            {renderFormHeadlineAndTitle(props)}
                            {renderLobsterData(lobsterData, props, error, errorElement,
                                               lowerThanWindowLarge, lowerThanWindowSmall)}
                            {renderPicture(props)}
                            {renderPeriodText(props)}
                            {renderPrivacyInfo(props, t)}
                            {renderSubmitButton(
                                props,
                                lowerThanWindowLarge,
                                focus,
                                setFocus,
                                clicked,
                                "increase-donation-period",
                                formTracking.onSubmitClick
                            )}
                            {renderFormErrorArea(errorBox, setErrorBox, t)}
                        </Grid>
                    </form>
                </CCOContainer>}
        </CCOComponentWrapper>
    );
}

export default F008PeriodIncreaseForm;